import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import countryList from "react-select-country-list";
import "react-datepicker/dist/react-datepicker.css";
import "./PersonalInformation.css";

function PersonalInformation({
  onBack,
  onContinue,
  initialData = null,
  error = null,
  ageLimit = 18,
}) {
  const [formData, setFormData] = useState({
    fullName: initialData?.fullName || "",
    dateOfBirth: initialData?.dateOfBirth || "",
    country: initialData?.country || "Pakistan",
  });
 
  const [localError, setLocalError] = useState(null);

  // Helper to parse date string (YYYY-MM-DD) to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper to format Date object to string (YYYY-MM-DD)
  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(
    parseDate(initialData?.dateOfBirth),
  );

  // Update form data if initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        fullName: initialData.fullName || "",
        dateOfBirth: initialData.dateOfBirth || "",
        country: initialData.country || "Pakistan",
      });
      setSelectedDate(parseDate(initialData.dateOfBirth));
    }
  }, [initialData]);

  const countryObjectList = useMemo(() => countryList().getData(), []);

  // Helper to convert country ISO code to flag emoji
  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return "";
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  // Set country object list for the select options format: { value: 'XX', label: 'Country Name', code: 'ISO' }
  const countryOptions = useMemo(() => {
    return countryObjectList.map((country) => ({
      value: country.label, // Storing full name as value to keep backward compatibility with existing state
      label: country.label,
      code: country.value, // ISO code (e.g., 'PK')
    }));
  }, [countryObjectList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: formatDate(date),
    }));
  };
 
  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
 
  const handleSubmit = () => {
    if (formData.fullName && formData.dateOfBirth && formData.country) {
      if (ageLimit && calculateAge(formData.dateOfBirth) < ageLimit) {
        setLocalError(`You must be at least ${ageLimit} years old to proceed.`);
        return;
      }
      setLocalError(null);
      onContinue(formData);
    }
  };

  return (
    <div className="personal-container">
      <div className="personal-card">
        <div className="personal-header">
          <button className="back-button" onClick={onBack}>
            ←
          </button>
          <div className="header-progress">
            <div
              className="header-progress-fill"
              style={{ width: "33%" }}
            ></div>
          </div>
        </div>

        <div>
          <h2 className="personal-title">Personal Information</h2>
          <p className="personal-subtitle">
            Please provide your details as they appear on your ID
          </p>
          <div className="form-container">
            <div className="form-group">
              <label className="form-label">
                <svg
                  className="label-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="7" r="4" strokeWidth="2" />
                </svg>
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                className="form-input"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <svg
                  className="label-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="18"
                    rx="2"
                    strokeWidth="2"
                  />
                  <line
                    x1="16"
                    y1="2"
                    x2="16"
                    y2="6"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8"
                    y1="2"
                    x2="8"
                    y2="6"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
                </svg>
                Date of Birth
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd mm yyyy"
                className="form-input"
                maxDate={
                  new Date(
                    new Date().setFullYear(
                      new Date().getFullYear() - (ageLimit || 0),
                    ),
                  )
                }
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                dropdownMode="select"
                required
                popperPlacement="bottom-start"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <svg
                  className="label-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <path d="M2 12h20" strokeWidth="2" />
                  <path
                    d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"
                    strokeWidth="2"
                  />
                </svg>
                Country
              </label>
              <div className="country-input-wrapper">
                <span className="country-flag">
                  {getFlagEmoji(
                    countryOptions.find((opt) => opt.value === formData.country)
                      ?.code,
                  )}
                </span>
                <Select
                  name="country"
                  options={countryOptions}
                  value={countryOptions.find(
                    (option) => option.value === formData.country,
                  )}
                  onChange={(selectedOption) =>
                    handleChange({
                      target: { name: "country", value: selectedOption.value },
                    })
                  }
                  placeholder="Select Country"
                  className="country-select-container"
                  classNamePrefix="country-select"
                  isSearchable={true}
                  formatOptionLabel={({ label, code }, { context }) =>
                    context === "menu" ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "10px", fontSize: "18px" }}>
                          {getFlagEmoji(code)}
                        </span>
                        <span>{label}</span>
                      </div>
                    ) : (
                      <span>{label}</span>
                    )
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "transparent",
                      border: "none",
                      boxShadow: "none",
                      cursor: "pointer",
                      width: "100%",
                      minHeight: "auto",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0",
                    }),
                    input: (base) => ({
                      ...base,
                      margin: "0",
                      padding: "0",
                      color: "#333",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 100,
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                    }),
                    option: (base, state) => ({
                      ...base,
                      cursor: "pointer",
                      backgroundColor: state.isSelected
                        ? "#199d71"
                        : state.isFocused
                          ? "rgba(25, 157, 113, 0.1)"
                          : "white",
                      color: state.isSelected ? "#ffffff" : "#333",
                      padding: "10px 16px",
                      ":active": {
                        backgroundColor: "#199d71",
                        color: "#ffffff",
                      },
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#333",
                      fontWeight: "500",
                      fontSize: "14px",
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: "0",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {(error || localError) && (
            <div className="form-error">
              <svg
                className="error-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="12"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="12"
                  y1="16"
                  x2="12.01"
                  y2="16"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span>{error || localError}</span>
            </div>
          )}

          <div className="info-notice">
            <svg
              className="info-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
              <line
                x1="12"
                y1="16"
                x2="12"
                y2="12"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="12"
                cy="8"
                r="0.5"
                fill="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span>Make sure your information matches your ID document</span>
          </div>
        </div>

        <div className="personal-footer">
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={
              !formData.fullName || !formData.dateOfBirth || !formData.country
            }
          >
            Continue
          </button>

          <div className="personal-footer">Secured by VerifiLite</div>
        </div>
      </div>
    </div>
  );
}

export default PersonalInformation;
